import React, { useState, useEffect, useRef } from 'react';
import { Modal, Form, Row, Col, Tabs, Tab, Button, Alert, Card, InputGroup, ListGroup } from '@/components/ui';
import { RoutineTask, CreateProjectRoutineTask } from '../types/RoutineTask';
import { Project } from '../types/Project';
import MultiTierDropdown from './MultiTierDropdown';
import { routineTaskService, CreateRoutineTaskDTO } from '../services/routineTaskService';
import { projectService } from '../services/projectService';
import { areasService, ForemanArea, ContractArea } from '../services/areasService';
import { assetService } from '../services/assetService';
import { Asset } from '../types/Asset';
import GoogleGPSMap from './GoogleGPSMap';
import { ASSET_TYPES, ASSET_TYPE_LABELS } from '../constants/assetTypes';
import { Activity } from '../types/Activity';
import { getFieldLockingState, getLockedFieldStyle, canEditInternalReview } from '../utils/fieldLocking';
import { sortAssetsAlphabetically } from '../utils/assetUtils';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../contexts/AuthContext';
import { TaskStage } from '../types/Task';
import { employeeService } from '../services/employeeService';
import { User } from '../types/User';
import { MultiSelectDropdown } from './MultiSelectDropdown';
import { attachmentService } from '../services/attachmentService';
import type { TaskAttachment } from '../types/Task';
import TaskImageAttachments from '../features/quantified-tracking/components/taskModals/TaskImageAttachments';
import formAttachmentService, { FormAttachment } from '../services/formAttachmentService';
import type { RoutineInitialData } from '../features/work-id/utils/workIdToTaskInitialData';
import RelatedWorkIdsTab, { hasLinkedWorkIds } from './RelatedWorkIdsTab';

interface RoutineTaskModalProps {
  visible: boolean;
  onClose: () => void;
  onTaskSaved: (task: RoutineTask) => void;
  task?: RoutineTask;
  project?: Project;
  defaultStatus?: string;
  initialDataFromWorkId?: RoutineInitialData;
  /** When false, do not auto-populate Base Task from standing tasks (e.g. after convert-to-routine). Default true. */
  /**
   * Whether to auto-populate the Base Task field from standing tasks
   * when editing an existing routine task. Defaults to true.
   * For tasks converted from quantified to routine, this should be false
   * so the user can freely choose a base task without interference.
   */
  autoPopulateBaseTask?: boolean;
  /**
   * Create only: pre-select this standing task as the Base Task (HE C2 – a Standing Routine task
   * picked on a hired equipment line becomes a new Specific task built from it).
   */
  initialBaseTask?: { id: number; description?: string | null };
  /** Create only: a short note shown above the form, e.g. why the window opened. */
  notice?: React.ReactNode;
}

type TaskStatus = 'PROPOSED' | 'PLANNED' | 'ACTIVE' | 'CLOSED' | 'CANCELLED';

// V3-020 / FORM-01: every missing field shows under itself at once, the tab holding it shows a
// count, and focus moves to the first one.
type RoutineFieldKey = 'name' | 'foremanAreaId' | 'contractAreaId' | 'assetType' | 'assetId' | 'startLocation';
const ROUTINE_FIELD_ORDER: RoutineFieldKey[] = ['foremanAreaId', 'name', 'contractAreaId', 'assetType', 'assetId', 'startLocation'];
const ROUTINE_FIELD_TAB: Record<RoutineFieldKey, 'basic' | 'location'> = {
  name: 'basic',
  foremanAreaId: 'basic',
  contractAreaId: 'location',
  assetType: 'location',
  assetId: 'location',
  startLocation: 'location',
};
const routineFieldId = (key: RoutineFieldKey) => `routine-task-field-${key}`;
function focusRoutineField(key: RoutineFieldKey) {
  const el = document.getElementById(routineFieldId(key));
  if (!el) return;
  const target = el.matches('input, select, textarea, button')
    ? el
    : el.querySelector<HTMLElement>('input:not([type="hidden"]), select, textarea, button');
  const focusEl = target || el;
  focusEl.focus();
  focusEl.scrollIntoView?.({ block: 'center' });
}

const RequiredLabel: React.FC<{ text: string }> = ({ text }) => (
  <Form.Label>
    <strong>{text} *</strong>
  </Form.Label>
);

interface GPSMapProps {
  startLocation?: { lat: number; lng: number };
  endLocation?: { lat: number; lng: number };
  onLocationSelect: (location: { lat: number; lng: number }, type: 'start' | 'end') => void;
  locationType: 'A' | 'B' | 'C' | null;
}

// Utility function to format text in sentence case
const formatSentenceCase = (text: string): string => {
  if (!text) return '';
  // Handle underscore case first (e.g., "some_text" -> "Some Text")
  const withoutUnderscores = text.replace(/_/g, ' ');
  // Split into words, capitalize first letter of each word
  return withoutUnderscores
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Utility function to safely format date for input field
const formatDateForInput = (date: string | Date | undefined | null): string => {
  if (!date) return '';
  
  try {
    // If it's already a string in YYYY-MM-DD format, return it
    if (typeof date === 'string') {
      if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return date;
      }
      // If it contains 'T', split and return date part
      if (date.includes('T')) {
        return date.split('T')[0];
      }
      // Try to parse it as a date
      const parsed = new Date(date);
      if (!isNaN(parsed.getTime())) {
        return parsed.toISOString().split('T')[0];
      }
      return date;
    }
    
    // If it's a Date object, format it
    if (date instanceof Date) {
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    }
    
    return '';
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

const RoutineTaskModal: React.FC<RoutineTaskModalProps> = ({
  visible,
  onClose,
  onTaskSaved,
  task,
  project,
  defaultStatus,
  initialDataFromWorkId,
  autoPopulateBaseTask = true,
  initialBaseTask,
  notice
}) => {
  const { hasPermission } = usePermissions();
  const { currentUser } = useAuth();
  const canCreateTask = hasPermission('routine:specific_create');
  const isAdminCreating = canCreateTask && !task;
  const [activeTab, setActiveTab] = useState('basic');
  const showRelatedWorkIdsTab = !!task?.id && hasLinkedWorkIds(task);
  // The tab disappears once the last Work ID is unlinked; fall back to Basic Info.
  useEffect(() => {
    if (activeTab === 'related_work_ids' && !showRelatedWorkIdsTab) setActiveTab('basic');
  }, [activeTab, showRelatedWorkIdsTab]);
  const [showLocationTab, setShowLocationTab] = useState(false);
  const [locationType, setLocationType] = useState<'A' | 'B' | 'C' | null>(null);
  
  // Compute isAdminCreatingWithAB after locationType is declared
  const isAdminCreatingWithAB = isAdminCreating && (locationType === 'A' || locationType === 'B');
  const [formData, setFormData] = useState({
    status: task?.status || defaultStatus || 'PLANNED',
    priority: task?.priority || '',
    projectId: project?.id || task?.projectId || null,
    taskName: '',
    name: task?.name || '',
    description: task?.description || '',
    activities: task?.activities?.map(a => a.id) || [],
    internalNotes: task?.internalNotes || '',
    instructions: task?.instructions || '',
    foremanAreaId: task?.foremanAreaId || undefined,
    contractAreaId: undefined as number | undefined,
    assetType: '',
    assetId: undefined as number | undefined,
    startLatitude: '',
    startLongitude: '',
    endLatitude: '',
    endLongitude: '',
    taskSubType: task?.taskSubType || undefined,
    clonedTaskId: undefined as number | undefined,
    labourAllocation: task?.labourAllocation || 100,
    materialAllocation: task?.materialAllocation || 100,
    equipmentAllocation: task?.equipmentAllocation || 100,
    secondaryLabourAllocation: task?.secondaryLabourAllocation || null,
    secondaryMaterialAllocation: task?.secondaryMaterialAllocation || null,
    secondaryEquipmentAllocation: task?.secondaryEquipmentAllocation || null,
    completionAccomplishment: task?.completionAccomplishment || 0,
    startDate: formatDateForInput(task?.startDate),
    completionDate: formatDateForInput(task?.completionDate),
    externalComment: '', // External comment field for task creation
    assignedEmployeeIds: task?.assignedEmployees?.map(emp => emp.id) || []
  });

  const [standingTasks, setStandingTasks] = useState<RoutineTask[]>([]);
  // Whether the Base Task list has finished loading, and whether it failed. An empty dropdown
  // is indistinguishable from a broken one, which is exactly how this was reported ("I am not
  // able to select an activity, although I have the permission to create a task") — the list
  // was empty and the control said nothing about why. Base Task drives the activities for the
  // whole form, so there is no way to proceed without it.
  const [standingTasksLoading, setStandingTasksLoading] = useState(false);
  const [standingTasksError, setStandingTasksError] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [foremanAreas, setForemanAreas] = useState<ForemanArea[]>([]);
  const [contractAreas, setContractAreas] = useState<ContractArea[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [roadAssets, setRoadAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(false);
  // Validation and save failures render inline (see Modal.Body). They used to go through
  // window.alert, which blocks the page, shows one problem at a time, and leaves no trace
  // of which field is at fault once dismissed.
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<RoutineFieldKey, string>>>({});
  const [showMap, setShowMap] = useState(false);
  const [assetLoading, setAssetLoading] = useState(false);
  const [roadAssetsLoading, setRoadAssetsLoading] = useState(false);

  const [attachments, setAttachments] = useState<TaskAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  
  // Add state for employees
  const [employees, setEmployees] = useState<User[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [showForemanAreaWarning, setShowForemanAreaWarning] = useState(false);
  const employeeFetchRequestIdRef = useRef(0);
  const [attachmentNotes, setAttachmentNotes] = useState('');
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<number | null>(null);
  const [formImages, setFormImages] = useState<FormAttachment[]>([]);

  // Field locking logic based on task state and user permissions
  const canEditInternalReviewPermission = canEditInternalReview(hasPermission);
  
  const fieldLockingState = getFieldLockingState({
    taskStatus: task?.status as TaskStage,
    hasEditInternalReviewPermission: canEditInternalReviewPermission
  });
  
  const isTaskLocked = fieldLockingState.isLocked;
  const lockedFieldStyle = getLockedFieldStyle(isTaskLocked);

  useEffect(() => {
    if (visible) {
      loadStandingTasks();
      loadForemanAreas();
      loadContractAreas();

      // Initialize form data when task is provided
      if (task) {
        const taskAsset = task.taskLocations?.[0]?.asset;
        const startLocation = task.taskLocations?.find(loc => loc.type === 'START');
        const endLocation = task.taskLocations?.find(loc => loc.type === 'END');

        // Determine location type from task's activities
        const newLocationType = determineLocationType(task.activities);
        setLocationType(newLocationType);
        setShowLocationTab(!!newLocationType);

        setFormData({
          ...formData,
          status: task.status || defaultStatus || 'PLANNED',
          priority: task.priority || '',
          projectId: project?.id || task.projectId || null,
          taskName: '',
          name: task.name || '',
          description: task.description || '',
          activities: task.activities?.map(a => a.id) || [],
          internalNotes: task.internalNotes || '',
          instructions: task.instructions || '',
          foremanAreaId: task.foremanAreaId || undefined,
          contractAreaId: task.contractAreaId,
          assetType: taskAsset?.assetType || '',
          assetId: startLocation?.assetId || undefined,
          startLatitude: startLocation?.latitude?.toString() || '',
          startLongitude: startLocation?.longitude?.toString() || '',
          endLatitude: endLocation?.latitude?.toString() || '',
          endLongitude: endLocation?.longitude?.toString() || '',
          taskSubType: task.taskSubType || undefined,
          clonedTaskId: task.clonedTaskId || undefined,
          labourAllocation: task.labourAllocation || 100,
          materialAllocation: task.materialAllocation || 100,
          equipmentAllocation: task.equipmentAllocation || 100,
          secondaryLabourAllocation: task.secondaryLabourAllocation || null,
          secondaryMaterialAllocation: task.secondaryMaterialAllocation || null,
          secondaryEquipmentAllocation: task.secondaryEquipmentAllocation || null,
          completionAccomplishment: task.completionAccomplishment || 0,
          startDate: formatDateForInput(task.startDate),
          completionDate: formatDateForInput(task.completionDate),
          assignedEmployeeIds: task.assignedEmployees?.map(emp => emp.id) || []
        });

        // Load contract areas if foreman area is set
        if (task.foremanAreaId) {
          loadContractAreas();
        }

        // If we have an assetId but no asset type (from taskLocations), load the asset details
        if (startLocation?.assetId && !taskAsset?.assetType) {
          loadAssetDetails(startLocation.assetId);
        }
        // If we have both assetId and asset type, load the assets list
        else if (task.foremanAreaId && taskAsset?.assetType) {
          setFormData(prev => ({
            ...prev,
            assetType: taskAsset.assetType
          }));
          loadAssets();
        }

        // Load road assets if contract area is set
        if (task.contractAreaId) {
          loadAllAssets();
        }

        // Load attachments for existing task
        if (task.id) {
          loadAttachments(task.id.toString());
          loadFormImages(task.id);
        }
      } else {
        const startLoc = initialDataFromWorkId?.taskLocations?.[0];
        setFormData(prev => ({
          ...prev,
          status: defaultStatus || 'PLANNED',
          ...(initialDataFromWorkId && {
            activities: initialDataFromWorkId.activityIds ?? prev.activities,
            foremanAreaId: initialDataFromWorkId.foremanAreaId ?? prev.foremanAreaId,
            contractAreaId: initialDataFromWorkId.contractAreaId ?? prev.contractAreaId,
            completionAccomplishment: initialDataFromWorkId.completionAccomplishment ?? prev.completionAccomplishment,
            ...(startLoc && {
              assetId: startLoc.assetId ?? prev.assetId,
              startLatitude: startLoc.latitude?.toString() ?? prev.startLatitude,
              startLongitude: startLoc.longitude?.toString() ?? prev.startLongitude
            })
          })
        }));
      }
    }
  }, [visible, task, project, defaultStatus, initialDataFromWorkId]);

  useEffect(() => {
    if (visible) {
      loadProjects();
    }
  }, [visible, formData.foremanAreaId, foremanAreas]);

  useEffect(() => {
    if (formData.contractAreaId && formData.assetType) {
      loadAssets();
    }
  }, [formData.contractAreaId, formData.assetType]);

  // Fetch active employees (only for specific tasks, not standing), filtered by foreman area
  useEffect(() => {
    const fetchEmployees = async () => {
      // Only fetch for specific tasks (not standing)
      const isSpecificTask = !task || task.mode?.toLowerCase() !== 'standing';
      if (visible && isSpecificTask) {
        const requestId = ++employeeFetchRequestIdRef.current;

        if (!formData.foremanAreaId) {
          if (employeeFetchRequestIdRef.current === requestId) {
            setEmployees([]);
            setLoadingEmployees(false);
          }
          return;
        }

        try {
          if (employeeFetchRequestIdRef.current === requestId) {
            setLoadingEmployees(true);
          }

          const foremanAreaId = formData.foremanAreaId;
          const serviceAreaId = foremanAreaId
            ? foremanAreas.find((fa) => fa.id === foremanAreaId)?.serviceAreaId
            : undefined;

          // Try strictest filter first (foreman + service area), then fall back to avoid empty dropdown.
          let activeEmployees = await employeeService.getActiveEmployeesForSelection(
            foremanAreaId,
            serviceAreaId,
            true
          );

          if (activeEmployees.length === 0) {
            activeEmployees = await employeeService.getActiveEmployeesForSelection(
              foremanAreaId,
              undefined,
              true
            );
          }

          if (activeEmployees.length === 0 && serviceAreaId) {
            activeEmployees = await employeeService.getActiveEmployeesForSelection(
              undefined,
              serviceAreaId,
              true
            );
          }

          if (activeEmployees.length === 0) {
            activeEmployees = await employeeService.getActiveEmployeesForSelection(undefined, undefined, true);
          }

          if (employeeFetchRequestIdRef.current === requestId) {
            setEmployees(activeEmployees);
          }
        } catch (error) {
          console.error('Error fetching employees:', error);
          if (employeeFetchRequestIdRef.current === requestId) {
            setEmployees([]);
          }
        } finally {
          if (employeeFetchRequestIdRef.current === requestId) {
            setLoadingEmployees(false);
          }
        }
      }
    };
    fetchEmployees();
  }, [visible, task?.mode, formData.foremanAreaId, foremanAreas]);

  useEffect(() => {
    if (formData.contractAreaId) {
      loadAllAssets();
    }
  }, [formData.contractAreaId]);

  // Update location tab visibility when location type changes (for admin creating with type A)
  useEffect(() => {
    if (locationType !== null) {
      // Show location tab for type A when admin is creating, or for B/C always
      setShowLocationTab(locationType !== 'A' || isAdminCreating);
    }
  }, [locationType, isAdminCreating]);

  // New useEffect to populate base task when editing and standing tasks are loaded
  useEffect(() => {
    // Skip auto-population in contexts (like convert-to-routine) where we want the user
    // to freely choose a base task without the dropdown being reset.
    if (!autoPopulateBaseTask) {
      return;
    }


    // Only populate if we have a task, standing tasks are loaded, and taskName is not already set
    if (task && standingTasks.length > 0 && !formData.taskName) {
      
      let baseTask: RoutineTask | undefined;
      
      // Method 1: Try to find by clonedTaskId if it exists
      if (task.clonedTaskId) {
        baseTask = standingTasks.find(standingTask => standingTask.id === task.clonedTaskId);
      }
      
      // Method 2: Try to find by activity number(s) - most reliable method
      if (!baseTask && task.activities && task.activities.length > 0) {
        const taskActivityNumbers = task.activities.map(a => a.activityNumber).filter(Boolean);
        
        baseTask = standingTasks.find(standingTask => {
          if (!standingTask.activities || standingTask.activities.length === 0) return false;
          
          const standingTaskActivityNumbers = standingTask.activities.map(a => a.activityNumber).filter(Boolean);
          
          // Check if any activity numbers match
          const hasMatchingActivity = taskActivityNumbers.some(taskActivityNum => 
            standingTaskActivityNumbers.includes(taskActivityNum)
          );
          
          // For better matching, we could also check if the primary activity (first one) matches
          const primaryActivityMatch = taskActivityNumbers.length > 0 && 
            standingTaskActivityNumbers.length > 0 && 
            taskActivityNumbers[0] === standingTaskActivityNumbers[0];
          
          return hasMatchingActivity || primaryActivityMatch;
        });
        
      }
      
      // Method 3: Try to find by description/name match (fallback)
      if (!baseTask && task.description) {
        baseTask = standingTasks.find(standingTask => 
          standingTask.description === task.description ||
          standingTask.name === task.description
        );
      }
      
      // Method 4: Try to match by the name field (extract base name from project-specific names)
      if (!baseTask && task.name) {
        // Extract the base task name from project-specific task names like "Task Name (Project)"
        const extractedName = task.name.replace(/\s*\([^)]*\)\s*$/, '').trim();
        baseTask = standingTasks.find(standingTask => 
          standingTask.description === extractedName ||
          standingTask.name === extractedName ||
          standingTask.description === task.name ||
          standingTask.name === task.name
        );
      }
      
      // Method 5: Try partial matching as last resort (in case of slight variations in naming)
      if (!baseTask && (task.description || task.name)) {
        const searchTerm = (task.description || task.name || '').replace(/\s*\([^)]*\)\s*$/, '').toLowerCase().trim();
        baseTask = standingTasks.find(standingTask => {
          const taskDesc = (standingTask.description || '').toLowerCase().trim();
          const taskName = (standingTask.name || '').toLowerCase().trim();
          return taskDesc.includes(searchTerm) || taskName.includes(searchTerm) ||
                 searchTerm.includes(taskDesc) || searchTerm.includes(taskName);
        });
      }
      
      if (baseTask) {
        setFormData(prev => ({
          ...prev,
          taskName: baseTask?.description || ''
        }));
      } else {
        console.warn('❌ Could not find matching base task for:', {
          taskId: task.id,
          clonedTaskId: task.clonedTaskId,
          description: task.description,
          name: task.name,
          activities: task.activities?.map(a => ({ id: a.id, activityNumber: a.activityNumber })),
          availableTasksCount: standingTasks.length,
          availableTasks: standingTasks.map(st => ({ 
            id: st.id, 
            description: st.description, 
            name: st.name,
            activities: st.activities?.map(a => ({ id: a.id, activityNumber: a.activityNumber }))
          })).slice(0, 5) // Show first 5 for debugging
        });
      }
    } else if (task && standingTasks.length === 0) {
    } else if (!task) {
    } else if (formData.taskName) {
    }
  }, [task, standingTasks, formData.taskName, autoPopulateBaseTask]);

  // Populate base task when creating from Work ID (initialDataFromWorkId has activityIds)
  useEffect(() => {
    if (!visible || task || !initialDataFromWorkId?.activityIds?.length || standingTasks.length === 0 || formData.taskName) {
      return;
    }
    const activityIds = initialDataFromWorkId.activityIds;
    const baseTask = standingTasks.find(st =>
      st.activities?.some(a => activityIds.includes(a.id))
    );
    if (baseTask) {
      const selectedProject = projects.find(p => p.id === formData.projectId);
      const projectName = selectedProject?.name || '';
      const taskName = projectName ? `${baseTask.description} (${projectName})` : baseTask.description;
      setFormData(prev => ({
        ...prev,
        taskName: baseTask.description || '',
        name: taskName || '',
        activities: baseTask.activities?.map(a => a.id) || [],
        clonedTaskId: baseTask.id
      }));
      const newLocationType = determineLocationType(baseTask.activities);
      setLocationType(newLocationType);
      setShowLocationTab(!!newLocationType || (isAdminCreating && newLocationType === 'A'));
    }
  }, [visible, task, initialDataFromWorkId?.activityIds, standingTasks, formData.taskName, formData.projectId, projects, isAdminCreating]);

  // HE C2: pre-select the standing task the user picked. The list is de-duplicated by description,
  // so fall back to a description match, but keep the picked task as the clone source.
  useEffect(() => {
    if (!visible || task || !initialBaseTask || standingTasks.length === 0 || formData.taskName) {
      return;
    }
    const baseTask =
      standingTasks.find(st => st.id === initialBaseTask.id) ??
      standingTasks.find(st => !!initialBaseTask.description && st.description === initialBaseTask.description);
    if (!baseTask) return;
    const selectedProject = projects.find(p => p.id === formData.projectId);
    const projectName = selectedProject?.name || '';
    const taskName = projectName ? `${baseTask.description} (${projectName})` : baseTask.description;
    setFormData(prev => ({
      ...prev,
      taskName: baseTask.description || '',
      name: taskName || '',
      activities: baseTask.activities?.map(a => a.id) || [],
      taskSubType: baseTask.taskSubType || undefined,
      clonedTaskId: initialBaseTask.id
    }));
    const newLocationType = determineLocationType(baseTask.activities);
    setLocationType(newLocationType);
    setShowLocationTab(!!newLocationType || (isAdminCreating && newLocationType === 'A'));
  }, [visible, task, initialBaseTask, standingTasks, formData.taskName, formData.projectId, projects, isAdminCreating]);

  const loadContractAreas = async () => {
    try {
      const areas = await areasService.getAllContractAreas();
      setContractAreas(areas);
    } catch (error) {
      console.error('Error loading contract areas:', error);
    }
  };

  const loadAttachments = async (taskId: string) => {
    try {
      const data = await attachmentService.getTaskAttachments(taskId);
      setAttachments(data);
    } catch (err) {
      console.error('Failed to load attachments:', err);
    }
  };

  const loadFormImages = async (taskIdNum: number) => {
    try {
      const imgs = await formAttachmentService.getAttachmentsForTask(taskIdNum);
      setFormImages(imgs || []);
    } catch (err) {
      console.error('Failed to load form images:', err);
      setFormImages([]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setPendingFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!task?.id || !pendingFile) return;
    setUploading(true);
    try {
      await attachmentService.uploadAttachment(task.id.toString(), pendingFile, attachmentNotes || undefined);
      setPendingFile(null);
      setAttachmentNotes('');
      await loadAttachments(task.id.toString());
    } catch (err) {
      console.error('Failed to upload attachment:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId: number) => {
    if (!attachmentId) return;
    setDeletingAttachmentId(attachmentId);
    try {
      await attachmentService.deleteAttachment(String(attachmentId));
      if (task?.id) {
        await loadAttachments(task.id.toString());
      }
    } catch (err) {
      console.error('Failed to delete attachment:', err);
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  const loadAllAssets = async () => {
    if (!formData.contractAreaId) return;
    
    setRoadAssetsLoading(true);
    try {
      // Load road assets for the road assets dropdown (this maintains existing functionality)
      const roads = await assetService.getAssetsByForemanArea(
        formData.foremanAreaId!,
        'road'
      );
      setRoadAssets(roads);
    } catch (error) {
      console.error('Error loading road assets:', error);
      setRoadAssets([]);
    } finally {
      setRoadAssetsLoading(false);
    }
  };

  const loadAssets = async () => {
    if (!formData.foremanAreaId || !formData.contractAreaId || (!formData.assetType && !task?.taskLocations?.[0]?.asset?.assetType)) return;
    
    setAssetLoading(true);
    try {
      const assetType = formData.assetType || task?.taskLocations?.[0]?.asset?.assetType;
      const loadedAssets = await assetService.searchAssets({
        foremanAreaId: formData.foremanAreaId,
        contractAreaId: formData.contractAreaId,
        assetType
      });
      
      setAssets(sortAssetsAlphabetically(loadedAssets));
    } catch (error) {
      console.error('Error loading assets:', error);
      setAssets([]);
    } finally {
      setAssetLoading(false);
    }
  };

  const loadAssetDetails = async (assetId: number) => {
    try {
      const asset = await assetService.getAssetById(assetId);
      if (asset) {
        setFormData(prev => ({
          ...prev,
          assetType: asset.assetType,
        }));
        // This will trigger loadAssets() through the useEffect
      }
    } catch (error) {
      console.error('Error loading asset details:', error);
    }
  };

  const handleLocationSelect = (lat: number, lng: number, type: 'start' | 'end') => {
    if (isTaskLocked) return; // Don't allow location updates for locked tasks
    if (type === 'start') {
      setFormData(prev => ({
        ...prev,
        startLatitude: lat.toString(),
        startLongitude: lng.toString()
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        endLatitude: lat.toString(),
        endLongitude: lng.toString()
      }));
    }
  };

  // Add this helper function to extract activity number
  const extractActivityNumber = (task: RoutineTask): number => {
    if (!task.activities || task.activities.length === 0) return Infinity;
    const activity = task.activities[0];
    if (!activity || !activity.activityNumber) return Infinity;
    
    // Extract numeric part from activity number
    const match = activity.activityNumber.match(/\d+/);
    return match ? parseInt(match[0]) : Infinity;
  };

  const loadStandingTasks = async () => {
    setStandingTasksLoading(true);
    setStandingTasksError(false);
    try {
      const tasks = await routineTaskService.getTasksByModeAndType('standing', 'Routine');

      // Sort tasks by activity number in ascending order
      const sortedTasks = [...tasks].sort((a, b) => {
        const aNumber = extractActivityNumber(a);
        const bNumber = extractActivityNumber(b);
        return aNumber - bNumber;
      });
      
      // Deduplicate tasks by description to match the dropdown behavior
      // Keep the first task for each unique description
      const deduplicatedTasks = sortedTasks.reduce((acc: RoutineTask[], current: RoutineTask) => {
        const exists = acc.find(task => task.description === current.description);
        if (!exists) {
          acc.push(current);
        }
        return acc;
      }, []);
      
      
      setStandingTasks(deduplicatedTasks);
    } catch (error) {
      console.error('Error loading standing tasks:', error);
      setStandingTasksError(true);
    } finally {
      setStandingTasksLoading(false);
    }
  };

  const loadProjects = async () => {
    try {
      const serviceAreaId = formData.foremanAreaId && foremanAreas.length > 0
        ? foremanAreas.find(fa => fa.id === formData.foremanAreaId)?.serviceAreaId
        : undefined;
      const projectsList = await projectService.getProjectsForTaskModal(
        serviceAreaId?.toString(),
        'active,planned'
      );
      const list = Array.isArray(projectsList) ? projectsList : [];
      if (project && !list.some((p: Project) => p.id === project.id)) {
        setProjects([project, ...list]);
      } else {
        setProjects(list);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
      setProjects([]);
    }
  };

  const loadForemanAreas = async () => {
    try {
      const areas = await areasService.getForemanAreas();
      setForemanAreas(areas);
    } catch (error) {
      console.error('Error loading foreman areas:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Special handling for numeric fields
    if (name === 'projectId') {
      setFormData(prev => ({
        ...prev,
        [name]: value ? parseInt(value) : null
      }));
      return;
    }
    
    if (name === 'foremanAreaId') {
      setShowForemanAreaWarning(false);
      setFormData(prev => ({
        ...prev,
        [name]: value ? parseInt(value) : undefined,
        assignedEmployeeIds: value ? prev.assignedEmployeeIds : []
      }));
      return;
    }

    if (name === 'contractAreaId') {
      setFormData(prev => ({
        ...prev,
        [name]: value ? parseInt(value) : undefined
      }));
      return;
    }

    if (name === 'completionAccomplishment') {
      setFormData(prev => ({
        ...prev,
        [name]: value !== '' ? parseFloat(value) || 0 : 0
      }));
      return;
    }

    if (name === 'startDate') {
      // When startDate changes, also update completionDate to the same value
      setFormData(prev => ({
        ...prev,
        startDate: value,
        completionDate: value // Update completionDate to match startDate
      }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Function to determine location type from activity
  const determineLocationType = (activities: Activity[] | undefined): 'A' | 'B' | 'C' | null => {
    
    if (!activities || activities.length === 0) {
      return null;
    }
    
    // Get the first activity's location code
    const locationCode = activities[0].locationCode?.toUpperCase() || '';
    
    if (locationCode.startsWith('A')) return 'A';
    if (locationCode.startsWith('B')) return 'B';
    if (locationCode.startsWith('C')) return 'C';
    return null;
  };

  const mapLocationTypeToGPSType = (type: 'A' | 'B' | 'C' | null): 'start' | 'end' => {
    return type === 'A' ? 'start' : 'end';
  };

  // Update handleTaskNameChange to set location visibility
  const handleTaskNameChange = (value: string) => {
    const selectedTask = standingTasks.find(task => task.description === value);
    
    if (selectedTask) {
      // Determine location type from activities
      const newLocationType = determineLocationType(selectedTask.activities);
      
      setLocationType(newLocationType);
      // Show location tab for type A when admin is creating, or for B/C always
      setShowLocationTab(!!newLocationType || (isAdminCreating && newLocationType === 'A'));

      // If we're editing an existing task, keep its name
      const keepExistingName = task?.name && task.name.length > 0;
      const taskName = keepExistingName ? task.name : (() => {
        const selectedProject = projects.find(p => p.id === formData.projectId);
        const projectName = selectedProject?.name || '';
        return projectName ? `${selectedTask.description} (${projectName})` : selectedTask.description;
      })();

      setFormData(prev => ({
        ...prev,
        taskName: selectedTask.description || '',
        name: taskName || '',
        activities: selectedTask.activities?.map(a => a.id) || [],
        taskSubType: selectedTask.taskSubType || undefined,
        clonedTaskId: selectedTask.id
      }));
    } else {
      setShowLocationTab(false);
      setLocationType(null);
      
      // If we're editing an existing task, keep its name
      const keepExistingName = task?.name && task.name.length > 0;
      const finalName = (keepExistingName ? task.name : value) || '';
      
      setFormData(prev => ({
        ...prev,
        taskName: value,
        name: finalName,
        activities: [],
        taskSubType: undefined,
        clonedTaskId: undefined
      }));
    }
  };

  // Add a useEffect to update the name when project changes
  useEffect(() => {
    if (formData.taskName && formData.projectId) {
      const selectedProject = projects.find(p => p.id === formData.projectId);
      const projectName = selectedProject?.name || '';
      if (projectName) {
        setFormData(prev => ({
          ...prev,
          name: `${prev.taskName} (${projectName})`
        }));
      }
    }
  }, [formData.projectId, projects]);

  /** Every missing required field at once (V3-020), not the first one in a banner. */
  const validateRoutineTask = (): Partial<Record<RoutineFieldKey, string>> => {
    const errors: Partial<Record<RoutineFieldKey, string>> = {};
    if (!formData.name?.trim()) errors.name = 'Task Name is required';
    if (!formData.foremanAreaId) errors.foremanAreaId = 'Foreman Area is required';
    // Validate asset if location tab is visible and type is B or C
    // For admin creating tasks with A/B activities, assets are optional
    if (showLocationTab && (locationType === 'B' || locationType === 'C') && !isAdminCreatingWithAB) {
      if (!formData.contractAreaId) errors.contractAreaId = 'Contract Area is required for this task type';
      if (!formData.assetType) errors.assetType = 'Asset Type is required for this task type';
      if (!formData.assetId) errors.assetId = 'Asset selection is required for this task type';
    }
    // For type C, assets are always required (point activities need GPS coordinates)
    if (showLocationTab && locationType === 'C' && (!formData.startLatitude || !formData.startLongitude)) {
      errors.startLocation = 'Starting location coordinates are required for point activities';
    }
    return errors;
  };

  // A fixed field loses its error as soon as it is valid.
  useEffect(() => {
    setFieldErrors((prev) => {
      const keys = Object.keys(prev) as RoutineFieldKey[];
      if (keys.length === 0) return prev;
      const current = validateRoutineTask();
      const next: Partial<Record<RoutineFieldKey, string>> = {};
      keys.forEach((k) => {
        if (current[k]) next[k] = current[k];
      });
      return Object.keys(next).length === keys.length ? prev : next;
    });
    // validateRoutineTask only reads formData and the location flags.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const routineTabErrorCount = (tab: 'basic' | 'location') =>
    (Object.keys(fieldErrors) as RoutineFieldKey[]).filter((k) => fieldErrors[k] && ROUTINE_FIELD_TAB[k] === tab).length;
  const routineTabTitle = (label: string, tab: 'basic' | 'location') => {
    const count = routineTabErrorCount(tab);
    return count > 0 ? (
      <>
        {label} <span className="tw-text-danger">({count})</span>
      </>
    ) : (
      label
    );
  };
  const routineFieldError = (key: RoutineFieldKey) =>
    fieldErrors[key] ? (
      <Form.Control.Feedback type="invalid" className="tw-block" id={`${routineFieldId(key)}-error`} style={{ display: 'block' }}>
        {fieldErrors[key]}
      </Form.Control.Feedback>
    ) : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const errors = validateRoutineTask();
    const firstError = ROUTINE_FIELD_ORDER.find((k) => errors[k]);
    setFieldErrors(errors);
    if (firstError) {
      setActiveTab(ROUTINE_FIELD_TAB[firstError]);
      // After the tab shows: focus the first field with an error, scrolled into view.
      setTimeout(() => focusRoutineField(firstError), 0);
      return;
    }
    setLoading(true);

    try {

      // Create task locations array
      const taskLocations = [];
      
      // Add start location if coordinates are provided
      if (formData.startLatitude && formData.startLongitude) {
        taskLocations.push({
          type: 'START' as const,
          latitude: Number(formData.startLatitude),
          longitude: Number(formData.startLongitude),
          assetId: formData.assetId
        });
      }

      // Add end location if coordinates are provided
      if (formData.endLatitude && formData.endLongitude) {
        taskLocations.push({
          type: 'END' as const,
          latitude: Number(formData.endLatitude),
          longitude: Number(formData.endLongitude),
          assetId: formData.assetId
        });
      }

      // Project ID is optional for routine tasks

      const taskData: CreateProjectRoutineTask = {
        description: formData.description,
        name: formData.name || formData.taskName || formData.description,
        projectId: formData.projectId || undefined,
        activities: formData.activities,
        status: formData.status.toUpperCase() as TaskStatus,
        mode: 'specific',
        taskSubType: formData.taskSubType,
        internalNotes: formData.internalNotes || '',
        instructions: formData.instructions || '',
        foremanAreaId: formData.foremanAreaId,
        priority: formData.priority || 'LOW',
        contractAreaId: formData.contractAreaId,
        taskLocations: taskLocations.length > 0 ? taskLocations : undefined,
        // Add default allocations
        labourAllocation: formData.labourAllocation || 100,
        materialAllocation: formData.materialAllocation || 100,
        equipmentAllocation: formData.equipmentAllocation || 100,
        secondaryLabourAllocation: formData.secondaryLabourAllocation || null,
        secondaryMaterialAllocation: formData.secondaryMaterialAllocation || null,
        secondaryEquipmentAllocation: formData.secondaryEquipmentAllocation || null,
        clonedTaskId: formData.clonedTaskId ? Number(formData.clonedTaskId) : null,
        completionAccomplishment: formData.completionAccomplishment !== undefined ? formData.completionAccomplishment : undefined,
        startDate: formData.startDate || undefined,
        completionDate: formData.completionDate || undefined,
        assignedEmployeeIds: formData.assignedEmployeeIds || []
      };

      let updatedTask;
      if (task) {
        updatedTask = await routineTaskService.updateRoutineTask(task.id, taskData);
      } else {
        updatedTask = await routineTaskService.create(taskData);
        
        // Add external comment if provided during creation
        if (formData.externalComment && formData.externalComment.trim() && currentUser?.id) {
          try {
            // addHistoryComment returns the updated task with history
            updatedTask = await routineTaskService.addHistoryComment(updatedTask.id, {
              comments: formData.externalComment.trim(),
              userId: currentUser.id,
              isExternal: true
            });
          } catch (error) {
            console.error('Error adding external comment:', error);
            // Don't fail the whole operation if comment fails - task was already created
          }
        }
      }

      onTaskSaved(updatedTask);
      onClose();
    } catch (error) {
      console.error('Error saving task:', error);
      setSubmitError(error instanceof Error ? error.message : 'Failed to save task. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const readOnlyStyle = {
    backgroundColor: '#e9ecef',
    cursor: 'not-allowed'
  };

  const getParentAssetDescription = (parentAssetNumber: string) => {
    const parentAsset = roadAssets.find(asset => asset.assetNumber === parentAssetNumber);
    if (parentAsset) {
      return `${parentAsset.name} (#${parentAsset.assetNumber})`;
    }
    return `Asset #${parentAssetNumber}`;
  };

  const handleAssetSelect = (assetId: number) => {
    const selectedAsset = assets.find(asset => asset.id === assetId);
    if (selectedAsset) {
      setFormData(prev => ({
        ...prev,
        assetId,
        startLatitude: selectedAsset.startLatitude?.toString() || '',
        startLongitude: selectedAsset.startLongitude?.toString() || '',
        endLatitude: selectedAsset.endLatitude?.toString() || '',
        endLongitude: selectedAsset.endLongitude?.toString() || ''
      }));
    }
  };

  return (
    <Modal
      show={visible}
      onHide={onClose}
      size="xl"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>{task ? (isTaskLocked ? 'View Routine Task' : 'Edit Routine Task') : 'Add Routine Task'}</Modal.Title>
      </Modal.Header>
      <Modal.Body className={task && isTaskLocked ? 'wrap-readonly-form' : ''}>
        {!task && notice && (
          <Alert variant="info" className="tw-mb-3">
            {notice}
          </Alert>
        )}
        {submitError && (
          <Alert variant="danger" role="alert" className="tw-mb-3">
            {submitError}
          </Alert>
        )}
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => k && setActiveTab(k)}
          className="tw-mb-3"
        >
          <Tab eventKey="basic" title={routineTabTitle('Basic Info', 'basic')}>
            <Form onSubmit={handleSubmit}>
              {task && (
                <>
                  <Row className="tw-mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label><strong>Completion Accomplishment</strong></Form.Label>
                        <Form.Control
                          type="number"
                          name="completionAccomplishment"
                          value={formData.completionAccomplishment}
                          onChange={handleChange}
                          readOnly={formData.status !== 'ACTIVE'}
                          disabled={formData.status !== 'ACTIVE'}
                          style={formData.status !== 'ACTIVE' ? readOnlyStyle : undefined}
                          step="any"
                          min="0"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label><strong>Start Date</strong></Form.Label>
                        <Form.Control
                          type="date"
                          name="startDate"
                          value={formData.startDate}
                          onChange={handleChange}
                          readOnly={formData.status !== 'ACTIVE'}
                          disabled={formData.status !== 'ACTIVE'}
                          style={formData.status !== 'ACTIVE' ? readOnlyStyle : undefined}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="tw-mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label><strong>Completion Date</strong></Form.Label>
                        <Form.Control
                          type="date"
                          name="completionDate"
                          value={formData.completionDate}
                          onChange={handleChange}
                          readOnly={formData.status !== 'ACTIVE'}
                          disabled={formData.status !== 'ACTIVE'}
                          style={formData.status !== 'ACTIVE' ? readOnlyStyle : undefined}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="tw-mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>
                          <strong>Task ID</strong>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={task.taskNumber || task.id}
                          readOnly
                          disabled
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </>
              )}

              {/* Show date fields and completion accomplishment when creating new task */}
              {!task && (
                <>
                  <Row className="tw-mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label><strong>Start Date</strong></Form.Label>
                        <Form.Control
                          type="date"
                          name="startDate"
                          value={formData.startDate}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label><strong>Completion Date</strong></Form.Label>
                        <Form.Control
                          type="date"
                          name="completionDate"
                          value={formData.completionDate}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="tw-mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label><strong>Completion Accomplishment</strong></Form.Label>
                        <Form.Control
                          type="number"
                          name="completionAccomplishment"
                          value={formData.completionAccomplishment}
                          onChange={handleChange}
                          step="any"
                          min="0"
                          placeholder="0"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </>
              )}

              {/* Employee Assignment - Only for Specific tasks (not Standing) */}
              {(!task || task.mode?.toLowerCase() !== 'standing') && (
                <Row className="tw-mb-3">
                  <Col md={12}>
                    <Form.Group>
                      <div
                        onClick={() => {
                          if (!formData.foremanAreaId) {
                            setShowForemanAreaWarning(true);
                          }
                        }}
                      >
                        <MultiSelectDropdown<User>
                          options={employees}
                          value={formData.assignedEmployeeIds || []}
                          onChange={(selectedIds) => {
                            setFormData(prev => ({
                              ...prev,
                              assignedEmployeeIds: selectedIds.map(id => Number(id))
                            }));
                          }}
                          formatOption={(employee) => `${employee.name} ${employee.lastName}${employee.employeeNumber ? ` (${employee.employeeNumber})` : ''}`}
                          formatDisplay={(employee) => `${employee.name} ${employee.lastName}${employee.employeeNumber ? ` (${employee.employeeNumber})` : ''}`}
                          getValue={(employee) => employee.id}
                          placeholder={
                            !formData.foremanAreaId
                              ? "Select Foreman Area first"
                              : loadingEmployees
                                ? "Loading employees..."
                                : employees.length === 0
                                  ? "No active employees found"
                                  : "Select employees"
                          }
                          disabled={isTaskLocked || loadingEmployees || !formData.foremanAreaId}
                          readOnly={isTaskLocked}
                          readOnlyStyle={lockedFieldStyle}
                          label="Assigned Employees"
                          isRequired={false}
                          noOptionsMessage={
                            !formData.foremanAreaId
                              ? "Select Foreman Area first"
                              : loadingEmployees
                                ? "Loading employees..."
                                : "No active employees found"
                          }
                          showSelectedCount={true}
                        />
                      </div>
                      {showForemanAreaWarning && !formData.foremanAreaId && (
                        <Form.Text className="tw-text-warning-text">
                          Please select a Foreman Area first to load Assigned Employees.
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              )}

              <Row className="tw-mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label><strong>Status *</strong></Form.Label>
                    <Form.Select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      disabled={isTaskLocked}
                      style={isTaskLocked ? lockedFieldStyle : undefined}
                    >
                      <option value="PROPOSED">{formatSentenceCase('PROPOSED')}</option>
                      <option value="PLANNED">{formatSentenceCase('PLANNED')}</option>
                      <option value="ACTIVE">{formatSentenceCase('ACTIVE')}</option>
                      <option value="CLOSED">{formatSentenceCase('CLOSED')}</option>
                      <option value="CANCELLED">{formatSentenceCase('CANCELLED')}</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label><strong>Priority *</strong></Form.Label>
                    <Form.Select
                      name="priority"
                      value={formData.priority}
                      onChange={handleChange}
                      disabled={isTaskLocked}
                      style={isTaskLocked ? lockedFieldStyle : undefined}
                    >
                      <option value="">Select Priority</option>
                      <option value="High">{formatSentenceCase('High')}</option>
                      <option value="Medium">{formatSentenceCase('Medium')}</option>
                      <option value="Low">{formatSentenceCase('Low')}</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="tw-mb-3">
                <Col md={6}>
                  <Form.Group>
                    <RequiredLabel text="Foreman Area" />
                    <Form.Select
                      id={routineFieldId('foremanAreaId')}
                      isInvalid={!!fieldErrors.foremanAreaId}
                      aria-describedby={fieldErrors.foremanAreaId ? `${routineFieldId('foremanAreaId')}-error` : undefined}
                      name="foremanAreaId"
                      value={formData.foremanAreaId?.toString() || ''}
                      onChange={handleChange}
                      required
                      disabled={isTaskLocked}
                      style={isTaskLocked ? lockedFieldStyle : undefined}
                    >
                      <option value="">Select Foreman Area</option>
                      {foremanAreas.map(area => (
                        <option key={area.id} value={area.id}>
                          {area.name}
                        </option>
                      ))}
                    </Form.Select>
                    {routineFieldError('foremanAreaId')}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label><strong>Project</strong></Form.Label>
                    {isTaskLocked ? (
                      <Form.Control
                        type="text"
                        readOnly
                        value={projects.find(p => p.id === formData.projectId)?.name ?? ''}
                        style={lockedFieldStyle}
                      />
                    ) : (
                      <Form.Select
                        name="projectId"
                        value={formData.projectId?.toString() || ''}
                        onChange={handleChange}
                      >
                        <option value="">Select Project</option>
                        {projects.map(proj => (
                          <option key={proj.id} value={proj.id}>
                            {proj.name}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Row className="tw-mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label><strong>Base Task *</strong></Form.Label>
                    <Form.Select
                      value={formData.taskName || ''}
                      onChange={(e) => handleTaskNameChange(e.target.value)}
                      required
                      disabled={isTaskLocked || standingTasksLoading || standingTasks.length === 0}
                      style={isTaskLocked ? lockedFieldStyle : undefined}
                    >
                      {/* Say which of the three states this is. Selecting a base task is what
                          supplies the activities for the rest of the form, so a silently empty
                          list reads as "I cannot select an activity" rather than as a load
                          failure the user could act on. */}
                      <option value="">
                        {standingTasksLoading
                          ? 'Loading tasks…'
                          : standingTasksError
                          ? 'Could not load tasks – close and reopen to retry'
                          : standingTasks.length === 0
                          ? 'No standing routine tasks are available'
                          : 'Select a task'}
                      </option>
                      {standingTasks.map((t) => (
                        <option key={t.id} value={t.description}>
                          ({t.taskNumber || 'No Task ID'}) - {t.description}
                        </option>
                      ))}
                    </Form.Select>
                    {(standingTasksError || (!standingTasksLoading && standingTasks.length === 0)) && (
                      <Form.Text className="tw-text-danger">
                        {standingTasksError
                          ? 'The standing task list failed to load, so activities cannot be selected.'
                          : 'No standing routine tasks were returned for your areas. Ask an administrator to check your access.'}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <RequiredLabel text="Task Name" />
                    <Form.Control
                      id={routineFieldId('name')}
                      isInvalid={!!fieldErrors.name}
                      aria-describedby={fieldErrors.name ? `${routineFieldId('name')}-error` : undefined}
                      type="text"
                      name="name"
                      value={formData.name || ''}
                      onChange={handleChange}
                      placeholder="Enter task name"
                      required
                      readOnly={isTaskLocked}
                      disabled={isTaskLocked}
                      style={isTaskLocked ? lockedFieldStyle : undefined}
                    />
                    {routineFieldError('name')}
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="tw-mb-3">
                <Form.Label><strong>Internal Notes / Description (Optional)</strong></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Enter internal notes here..."
                  readOnly={isTaskLocked}
                  disabled={isTaskLocked}
                  style={isTaskLocked ? lockedFieldStyle : undefined}
                />
              </Form.Group>

              <Form.Group className="tw-mb-3">
                <Form.Label><strong>Comments (Optional)</strong></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="internalNotes"
                  value={formData.internalNotes}
                  onChange={handleChange}
                  placeholder="Enter comments that will appear in D2 reports..."
                />
                <Form.Text className="tw-text-content-muted">
                  These comments will appear in the COMMENTS column of D2 reports.
                </Form.Text>
              </Form.Group>

              <Form.Group className="tw-mb-3">
                <Form.Label><strong>Instructions for Operator (Optional)</strong></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  name="instructions"
                  value={formData.instructions}
                  onChange={handleChange}
                  placeholder="Enter instructions for the operator here..."
                  readOnly={isTaskLocked}
                  disabled={isTaskLocked}
                  style={isTaskLocked ? lockedFieldStyle : undefined}
                />
              </Form.Group>

              {/* External Comment field - only shown when creating new task */}
              {!task && (
                <Form.Group className="tw-mb-3">
                  <Form.Label><strong>External Comment (Optional)</strong></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="externalComment"
                    value={formData.externalComment}
                    onChange={handleChange}
                    placeholder="Enter an external comment that will be visible to clients or external stakeholders..."
                  />
                  <Form.Text className="tw-text-content-muted">
                    External comments are visible to clients and external stakeholders in reports.
                  </Form.Text>
                </Form.Group>
              )}
            </Form>
          </Tab>

          {(showLocationTab && locationType !== 'A') || (isAdminCreating && locationType === 'A') ? (
            <Tab eventKey="location" title={routineTabTitle('Location', 'location')}>
              {(locationType === 'B' || locationType === 'C' || (isAdminCreating && locationType === 'A')) && (
                <Card className="tw-mb-4">
                  <Card.Header>
                    <h5 className="tw-mb-0">Asset Details</h5>
                  </Card.Header>
                  <Card.Body>
                    {/* Warning message for A/B coded activities with asset selected */}
                    {isAdminCreatingWithAB && formData.assetId && (
                      <Alert variant="warning" className="tw-mb-3">
                        <i className="bi bi-exclamation-triangle tw-mr-2"></i>
                        <strong>Warning:</strong> This task does not typically use an asset. You are adding an asset as an optional override.
                      </Alert>
                    )}

                    <Row className="tw-mb-3">
                      <Col md={6}>
                        {isAdminCreatingWithAB ? (
                          <Form.Label>Contract Area</Form.Label>
                        ) : (
                          <RequiredLabel text="Contract Area" />
                        )}
                        <Form.Select
                          id={routineFieldId('contractAreaId')}
                          isInvalid={!!fieldErrors.contractAreaId}
                          name="contractAreaId"
                          value={formData.contractAreaId}
                          onChange={handleChange}
                          required={!isAdminCreatingWithAB}
                          disabled={isTaskLocked}
                          style={isTaskLocked ? lockedFieldStyle : undefined}
                        >
                          <option value="">Select Contract Area</option>
                          {contractAreas.map(area => (
                            <option key={area.id} value={area.id}>
                              {area.name}
                            </option>
                          ))}
                        </Form.Select>
                        {routineFieldError('contractAreaId')}
                      </Col>
                      <Col md={6}>
                        {isAdminCreatingWithAB ? (
                          <Form.Label>Asset Type</Form.Label>
                        ) : (
                          <RequiredLabel text="Asset Type" />
                        )}
                        <Form.Select
                          id={routineFieldId('assetType')}
                          isInvalid={!!fieldErrors.assetType}
                          name="assetType"
                          value={formData.assetType}
                          onChange={handleChange}
                          required={!isAdminCreatingWithAB}
                          disabled={isTaskLocked}
                          style={isTaskLocked ? lockedFieldStyle : undefined}
                        >
                          <option value="">Select Asset Type</option>
                          {/* Sentence-casing the raw value rendered "restarea" as "Restarea";
                              ASSET_TYPE_LABELS is the same source the quantified modal reads. */}
                          <option value={ASSET_TYPES.ROAD}>{ASSET_TYPE_LABELS[ASSET_TYPES.ROAD]}</option>
                          <option value={ASSET_TYPES.STRUCTURE}>{ASSET_TYPE_LABELS[ASSET_TYPES.STRUCTURE]}</option>
                          <option value={ASSET_TYPES.RESTAREA}>{ASSET_TYPE_LABELS[ASSET_TYPES.RESTAREA]}</option>
                        </Form.Select>
                        {routineFieldError('assetType')}
                      </Col>
                    </Row>

                    <Row>
                      <Col>
                        {isAdminCreatingWithAB ? (
                          <Form.Label>Asset</Form.Label>
                        ) : (
                          <RequiredLabel text="Asset" />
                        )}
                        <div id={routineFieldId('assetId')}>
                        <MultiTierDropdown
                          items={assets}
                          value={formData.assetId}
                          onChange={handleAssetSelect}
                          disabled={isTaskLocked || !formData.assetType || assetLoading}
                          loading={assetLoading || roadAssetsLoading}
                          placeholder={
                            assetLoading || roadAssetsLoading
                              ? 'Loading assets...' 
                              : !formData.assetType 
                                ? 'Select an asset type first'
                                : 'Select Asset'
                          }
                          noItemsMessage="No assets found for the selected criteria"
                          getParentDescription={getParentAssetDescription}
                          assetType={formData.assetType}
                        />
                        </div>
                        {!formData.assetId && !isAdminCreatingWithAB && (
                          <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                            Asset selection is required
                          </Form.Control.Feedback>
                        )}
                      </Col>
                    </Row>

                    {/* Road Asset Information Display */}
                    {formData.assetId && formData.assetType?.toLowerCase() === 'road' && (() => {
                      const selectedAsset = assets.find(a => a.id === formData.assetId);
                      if (selectedAsset) {
                        return (
                          <Row className="tw-mt-3">
                            <Col>
                              <Card>
                                <Card.Header>
                                  <h6 className="tw-mb-0">
                                    <i className="bi bi-info-circle tw-mr-2"></i>
                                    Road Asset Information
                                  </h6>
                                </Card.Header>
                                <Card.Body>
                                  <Row>
                                    {selectedAsset.length && (
                                      <Col md={4}>
                                        <strong>Road Length:</strong>
                                        <br />
                                        <span className="tw-text-primary">{selectedAsset.length} km</span>
                                      </Col>
                                    )}
                                    {selectedAsset.summerClass && (
                                      <Col md={4}>
                                        <strong>Summer Classification:</strong>
                                        <br />
                                        <span className="tw-text-success">{selectedAsset.summerClass}</span>
                                      </Col>
                                    )}
                                    {selectedAsset.winterClass && (
                                      <Col md={4}>
                                        <strong>Winter Classification:</strong>
                                        <br />
                                        <span className="tw-text-warning-text">{selectedAsset.winterClass}</span>
                                      </Col>
                                    )}
                                  </Row>
                                </Card.Body>
                              </Card>
                            </Col>
                          </Row>
                        );
                      }
                      return null;
                    })()}
                  </Card.Body>
                </Card>
              )}

              {locationType === 'C' && (
                <Card>
                  <Card.Header>
                    <h5 className="tw-mb-0">Location Details</h5>
                  </Card.Header>
                  <Card.Body>
                    <div className="tw-mb-3">
                      <Button
                        type="button"
                        variant={showMap ? 'primary' : 'outline-primary'}
                        onClick={() => setShowMap(!showMap)}
                      >
                        {showMap ? 'Hide Map' : 'Use Map'}
                      </Button>
                    </div>

                    <div className="tw-mb-4" id={routineFieldId('startLocation')}>
                      <RequiredLabel text="Starting Location" />
                      <Row>
                        <Col md={6}>
                          <InputGroup>
                            <InputGroup.Text>Lat:</InputGroup.Text>
                            <Form.Control
                              type="number"
                              step="any"
                              name="startLatitude"
                              value={formData.startLatitude}
                              onChange={handleChange}
                              placeholder="Start Latitude"
                              required
                              readOnly={isTaskLocked}
                              disabled={isTaskLocked}
                              style={isTaskLocked ? lockedFieldStyle : undefined}
                            />
                          </InputGroup>
                        </Col>
                        <Col md={6}>
                          <InputGroup>
                            <InputGroup.Text>Lng:</InputGroup.Text>
                            <Form.Control
                              type="number"
                              step="any"
                              name="startLongitude"
                              value={formData.startLongitude}
                              onChange={handleChange}
                              placeholder="Start Longitude"
                              required
                              readOnly={isTaskLocked}
                              disabled={isTaskLocked}
                              style={isTaskLocked ? lockedFieldStyle : undefined}
                            />
                          </InputGroup>
                        </Col>
                      </Row>
                      {routineFieldError('startLocation')}
                    </div>

                    <div className="tw-mb-4">
                      <Form.Label>Ending Location (Optional)</Form.Label>
                      <Row>
                        <Col md={6}>
                          <InputGroup>
                            <InputGroup.Text>Lat:</InputGroup.Text>
                            <Form.Control
                              type="number"
                              step="any"
                              name="endLatitude"
                              value={formData.endLatitude}
                              onChange={handleChange}
                              placeholder="End Latitude"
                              readOnly={isTaskLocked}
                              disabled={isTaskLocked}
                              style={isTaskLocked ? lockedFieldStyle : undefined}
                            />
                          </InputGroup>
                        </Col>
                        <Col md={6}>
                          <InputGroup>
                            <InputGroup.Text>Lng:</InputGroup.Text>
                            <Form.Control
                              type="number"
                              step="any"
                              name="endLongitude"
                              value={formData.endLongitude}
                              onChange={handleChange}
                              placeholder="End Longitude"
                              readOnly={isTaskLocked}
                              disabled={isTaskLocked}
                              style={isTaskLocked ? lockedFieldStyle : undefined}
                            />
                          </InputGroup>
                        </Col>
                      </Row>
                    </div>

                    {showMap && (
                      <GoogleGPSMap
                        startLocation={formData.startLatitude && formData.startLongitude ? {
                          lat: Number(formData.startLatitude),
                          lng: Number(formData.startLongitude)
                        } : undefined}
                        endLocation={formData.endLatitude && formData.endLongitude ? {
                          lat: Number(formData.endLatitude),
                          lng: Number(formData.endLongitude)
                        } : undefined}
                        onLocationSelect={handleLocationSelect}
                        locationType={mapLocationTypeToGPSType(locationType)}
                        isPointActivity={locationType === 'C'}
                      />
                    )}
                  </Card.Body>
                </Card>
              )}
            </Tab>
          ) : null}
          <Tab eventKey="attachments" title="File Attachments" disabled={!task?.id}>
            {!task?.id ? (
              <Alert variant="info">Save the task first to add attachments.</Alert>
            ) : (
              <>
                <div className="tw-mb-3">
                  <Form.Label>Upload New Attachment</Form.Label>
                  <Form.Control
                    type="file"
                    className="tw-mb-2"
                    onChange={handleFileSelect}
                    disabled={uploading || isTaskLocked}
                    style={isTaskLocked ? lockedFieldStyle : undefined}
                  />
                  {pendingFile && (
                    <div className="tw-mb-2 tw-flex tw-gap-2 tw-items-center">
                      <span>Selected: {pendingFile.name}</span>
                    </div>
                  )}
                  <Form.Control
                    type="text"
                    className="tw-mb-2"
                    placeholder="Notes (optional)"
                    value={attachmentNotes}
                    onChange={(e: any) => setAttachmentNotes(e.target.value)}
                    disabled={uploading || isTaskLocked}
                    style={isTaskLocked ? lockedFieldStyle : undefined}
                  />
                  <Button
                    type="button"
                    variant="primary"
                    onClick={handleUpload}
                    disabled={!pendingFile || uploading || isTaskLocked}
                    style={isTaskLocked ? lockedFieldStyle : undefined}
                  >
                    {uploading ? 'Uploading...' : 'Upload'}
                  </Button>
                </div>

                <TaskImageAttachments 
                  attachments={attachments}
                  onDeleteAttachment={handleDeleteAttachment}
                  isTaskClosed={isTaskLocked}
                />

                {formImages.length > 0 && (
                  <div className="tw-mt-4">
                    <h6>Images from Daily Log</h6>
                    <div className="task-images-grid">
                      {formImages.map(img => {
                        const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace('/api', '');
                        const src = `${baseUrl}/api/forms/${img.formId}/attachments/${img.id}/download`;
                        return (
                          <div key={`${img.formId}-${img.id}`} className="task-image-item">
                            <img src={src} alt={img.filename} className="task-image-thumbnail" />
                            <div className="task-image-info">
                              <span className="task-image-filename">{img.filename}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {attachments.filter(a => !(a.attachmentType === 'image' || a.attachmentName.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|webp)$/))).length > 0 && (
                  <div className="tw-mt-4">
                    <h6>Other Attachments</h6>
                    <ListGroup>
                      {attachments.filter(a => !(a.attachmentType === 'image' || a.attachmentName.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|webp)$/))).map(att => (
                        <ListGroup.Item key={att.id} className="tw-flex tw-justify-between tw-items-center">
                          <span>{att.attachmentName}</span>
                          <div>
                            <Button
                              type="button"
                              variant="outline-secondary"
                              size="sm"
                              className="tw-mr-2"
                              onClick={() => attachmentService.downloadAttachment(String(att.id))}
                            >
                              Download
                            </Button>
                            {!isTaskLocked && (
                              <Button
                                type="button"
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDeleteAttachment(att.id ?? 0)}
                                disabled={deletingAttachmentId === att.id}
                              >
                                {deletingAttachmentId === att.id ? 'Deleting...' : 'Delete'}
                              </Button>
                            )}
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </div>
                )}
              </>
            )}
          </Tab>
          {/* Related Work IDs tab: shown only when the task has linked Work IDs; one card per Work ID */}
          {task?.id && showRelatedWorkIdsTab && (
            <Tab eventKey="related_work_ids" title="Related Work IDs">
              <RelatedWorkIdsTab
                workIds={task.workIds}
                taskId={task.id}
                onUnlink={hasPermission('workId:assign') ? async () => {
                  const updated = await routineTaskService.getTaskDetails(task.id);
                  onTaskSaved(updated);
                } : undefined}
              />
            </Tab>
          )}
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        {!isTaskLocked && (
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default RoutineTaskModal; 